import { useState, useEffect, useRef, useMemo } from "react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSave,
  faBox,
  faTags,
  faList,
  faBoxOpen,
  faBoxes,
  faSearch,
  faEdit,
} from "@fortawesome/free-solid-svg-icons";
import Modal from "../../../components/ui/modal";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../../../components/ui/select";
import { toastError, toastSuccess } from "../../../utils/toast";
import { productService } from "../../../services/service";

const FALLBACK_PACKAGE_TYPES = [
  "Box",
  "Carton",
  "Bundle",
  "Piece",
  "Bag",
  "Pallet",
  "Crate",
  "Other",
  "Bottle",
  "Can",
  "Jar",
];

const normalizeType = (t) => ({
  id: String(t?.productTypeId ?? t?.id ?? ""),
  name: t?.name ?? "",
  status: t?.status ?? "active",
});

const normalizeCategory = (c) => ({
  id: String(c?.productCategoryId ?? c?.id ?? ""),
  name: c?.category_name ?? c?.name ?? "",
  productTypeId: String(
    c?.product_type?.productTypeId ??
      c?.productTypeId ??
      c?.product_type_id ??
      "",
  ),
  status: c?.status ?? "active",
});

const EditProduct = ({
  isOpen,
  onClose,
  onSave,
  product,
  types = [],
  categories = [],
}) => {
  const [form, setForm] = useState({
    product_name: "",
    package_type: "",
    no_of_items: "",
    product_category_id: "",
    product_type_id: "",
  });
  const [errors, setErrors] = useState({});
  const [categorySearch, setCategorySearch] = useState("");
  const [typeSearch, setTypeSearch] = useState("");
  const [packageSearch, setPackageSearch] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [packageTypes, setPackageTypes] = useState(FALLBACK_PACKAGE_TYPES);
  const [packageTypesLoading, setPackageTypesLoading] = useState(false);
  const packageTypesLoaded = useRef(false);

  const [typeCategories, setTypeCategories] = useState([]);
  const [typeCategoriesLoading, setTypeCategoriesLoading] = useState(false);

  useEffect(() => {
    if (!isOpen || packageTypesLoaded.current) return;
    let mounted = true;

    (async () => {
      try {
        setPackageTypesLoading(true);
        const response = await productService.getPackageTypes();
        const raw = response?.data?.data ?? response?.data ?? [];
        const normalized = Array.isArray(raw)
          ? raw
              .map((item) =>
                typeof item === "string"
                  ? item
                  : item?.name || item?.package_type || null,
              )
              .filter(Boolean)
          : [];
        if (mounted) {
          setPackageTypes(
            normalized.length ? normalized : FALLBACK_PACKAGE_TYPES,
          );
          packageTypesLoaded.current = true;
        }
      } catch {
        if (mounted) setPackageTypes(FALLBACK_PACKAGE_TYPES);
      } finally {
        if (mounted) setPackageTypesLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [isOpen]);

  const normalizedTypes = useMemo(
    () => types.map(normalizeType).filter((t) => t.id),
    [types],
  );

  const fallbackCategories = useMemo(
    () =>
      form.product_type_id
        ? categories
            .map(normalizeCategory)
            .filter(
              (c) =>
                c.productTypeId === form.product_type_id &&
                c.status === "active",
            )
        : [],
    [categories, form.product_type_id],
  );

  useEffect(() => {
    if (!isOpen || !form.product_type_id) {
      setTypeCategories([]);
      return;
    }
    let mounted = true;

    (async () => {
      try {
        setTypeCategoriesLoading(true);
        const response = await productService.getProductCategoriesByProductType(
          form.product_type_id,
        );
        const raw = response?.data?.data ?? response?.data ?? [];
        const normalized = Array.isArray(raw)
          ? raw.map(normalizeCategory).filter((c) => c.id)
          : [];
        if (mounted) setTypeCategories(normalized);
      } catch {
        if (mounted) setTypeCategories([]);
      } finally {
        if (mounted) setTypeCategoriesLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [isOpen, form.product_type_id]);

  const filteredCategories = useMemo(() => {
    const source = typeCategories.length ? typeCategories : fallbackCategories;
    if (!categorySearch.trim()) return source;
    const term = categorySearch.toLowerCase();
    return source.filter((c) => c.name.toLowerCase().includes(term));
  }, [typeCategories, fallbackCategories, categorySearch]);

  useEffect(() => {
    if (isOpen && product) {
      const cat = product.product_category;
      const type = cat?.product_type;

      setForm({
        product_name: product.product_name || "",
        package_type: product.package_type || "",
        no_of_items: product.no_of_items?.toString() || "",
        product_category_id: String(cat?.productCategoryId ?? ""),
        product_type_id: String(type?.productTypeId ?? ""),
      });
      setCategorySearch("");
      setTypeSearch("");
      setPackageSearch("");
      setErrors({});
      setIsSubmitting(false);
      setTypeCategories([]);
    }
  }, [isOpen, product]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleTypeChange = (value) => {
    setForm((prev) => ({
      ...prev,
      product_type_id: value,
      product_category_id: "",
    }));
    setTypeCategories([]);
    setCategorySearch("");
    if (errors.product_type_id)
      setErrors((prev) => ({ ...prev, product_type_id: "" }));
  };

  const handleSubmit = () => {
    const newErrors = {};

    if (!form.product_name.trim())
      newErrors.product_name = "Product name is required";
    if (!form.package_type) newErrors.package_type = "Package type is required";
    if (!form.no_of_items || parseInt(form.no_of_items) < 0)
      newErrors.no_of_items = "Valid number of items is required";
    if (!form.product_category_id)
      newErrors.product_category_id = "Category is required";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toastError("Please fix all validation errors before continuing.");
      return;
    }

    setIsSubmitting(true);

    try {
      const result = onSave({
        productId: product.productId,
        product_name: form.product_name,
        package_type: form.package_type,
        no_of_items: parseInt(form.no_of_items),
        product_category_id: form.product_category_id,
      });

      if (result && typeof result.then === "function") {
        result
          .then(() => {
            toastSuccess(
              `Product "${form.product_name}" updated successfully!`,
            );
            setIsSubmitting(false);
            onClose();
          })
          .catch((error) => {
            toastError(
              `Failed to update product: ${error.message || "Please try again."}`,
            );
            setIsSubmitting(false);
          });
      } else {
        toastSuccess(`Product "${form.product_name}" updated successfully!`);
        setIsSubmitting(false);
        onClose();
      }
    } catch (error) {
      toastError(
        `Failed to update product: ${error.message || "Please try again."}`,
      );
      setIsSubmitting(false);
    }
  };

  const filteredTypes = useMemo(
    () =>
      normalizedTypes.filter(
        (t) =>
          t.status === "active" &&
          t.name.toLowerCase().includes(typeSearch.toLowerCase()),
      ),
    [normalizedTypes, typeSearch],
  );

  const filteredPackages = useMemo(
    () =>
      packageTypes.filter((pt) =>
        String(pt).toLowerCase().includes(packageSearch.toLowerCase()),
      ),
    [packageTypes, packageSearch],
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Edit product"
      maxWidth="sm:max-w-lg"
      footer={
        <div className="flex flex-col-reverse sm:flex-row justify-end gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
            className="w-full sm:w-auto px-6 border-2 border-orange-700 text-red-600 hover:text-white hover:bg-orange-600 hover:border-none"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="gap-2 w-full sm:w-auto bg-blue-800 hover:bg-blue-900 text-white px-6 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Updating...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faSave} />
                Update product
              </>
            )}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-blue-50 to-white rounded-lg border border-blue-100">
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
            <FontAwesomeIcon icon={faEdit} className="text-blue-800 h-4 w-4" />
          </div>
          <div>
            <p className="text-xs text-gray-600 font-bold">Edit product</p>
            <p className="text-xs text-gray-600 font-medium">
              Update product information
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-gray-700 font-medium flex items-center gap-2">
            <FontAwesomeIcon
              icon={faBox}
              className="text-blue-800 h-3.5 w-3.5"
            />
            Product name<span className="text-red-700">*</span>
          </label>
          <Input
            name="product_name"
            value={form.product_name}
            onChange={handleChange}
            placeholder="Enter product name"
            className={`border ${
              errors.product_name ? "border-red-500" : "border-gray-300"
            } rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-800/30`}
          />
          {errors.product_name && (
            <p className="text-red-500 text-xs flex items-center gap-1">
              <span className="w-1 h-1 bg-red-500 rounded-full"></span>
              {errors.product_name}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-gray-700 font-medium flex items-center gap-2">
            <FontAwesomeIcon
              icon={faTags}
              className="text-blue-800 h-3.5 w-3.5"
            />
            Product type<span className="text-red-700">*</span>
          </label>
          <Select value={form.product_type_id} onValueChange={handleTypeChange}>
            <SelectTrigger
              className={`w-full h-10 ${
                errors.product_type_id ? "border-red-500" : "border-gray-300"
              }`}
            >
              <SelectValue placeholder="Select product type" />
            </SelectTrigger>
            <SelectContent>
              <div className="sticky top-0 bg-white p-2 border-b border-gray-100">
                <div className="relative">
                  <FontAwesomeIcon
                    icon={faSearch}
                    className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400"
                  />
                  <Input
                    type="text"
                    placeholder="Search types..."
                    value={typeSearch}
                    onChange={(e) => setTypeSearch(e.target.value)}
                    className="pl-8 pr-3 py-1.5 h-8 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-800/30 w-full"
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              </div>
              {filteredTypes.map((t) => (
                <SelectItem key={t.id} value={t.id}>
                  {t.name}
                </SelectItem>
              ))}
              {filteredTypes.length === 0 && (
                <div className="px-3 py-2 text-sm text-gray-400 text-center">
                  No types found
                </div>
              )}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-gray-700 font-medium flex items-center gap-2">
            <FontAwesomeIcon
              icon={faList}
              className="text-blue-800 h-3.5 w-3.5"
            />
            Category<span className="text-red-700">*</span>
          </label>
          <Select
            value={form.product_category_id}
            onValueChange={(value) => {
              setForm((prev) => ({ ...prev, product_category_id: value }));
              if (errors.product_category_id)
                setErrors((prev) => ({ ...prev, product_category_id: "" }));
            }}
            disabled={!form.product_type_id || typeCategoriesLoading}
          >
            <SelectTrigger
              className={`w-full h-10 ${
                errors.product_category_id
                  ? "border-red-500"
                  : "border-gray-300"
              } ${!form.product_type_id ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              <SelectValue
                placeholder={
                  !form.product_type_id
                    ? "Please select a type first"
                    : typeCategoriesLoading
                      ? "Loading categories..."
                      : "Select category"
                }
              />
            </SelectTrigger>
            <SelectContent>
              <div className="sticky top-0 bg-white p-2 border-b border-gray-100">
                <div className="relative">
                  <FontAwesomeIcon
                    icon={faSearch}
                    className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400"
                  />
                  <Input
                    type="text"
                    placeholder="Search categories..."
                    value={categorySearch}
                    onChange={(e) => setCategorySearch(e.target.value)}
                    className="pl-8 pr-3 py-1.5 h-8 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-800/30 w-full"
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              </div>
              {filteredCategories.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}
                </SelectItem>
              ))}
              {filteredCategories.length === 0 && form.product_type_id && (
                <div className="px-3 py-2 text-sm text-gray-400 text-center">
                  No categories found for this type
                </div>
              )}
            </SelectContent>
          </Select>
          {errors.product_category_id && (
            <p className="text-red-500 text-xs flex items-center gap-1">
              <span className="w-1 h-1 bg-red-500 rounded-full"></span>
              {errors.product_category_id}
            </p>
          )}
          {form.product_type_id &&
            !typeCategoriesLoading &&
            filteredCategories.length === 0 && (
              <p className="text-amber-500 text-xs">
                No active categories found for this type
              </p>
            )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="flex flex-col gap-1">
            <label className="text-gray-700 font-medium flex items-center gap-2">
              <FontAwesomeIcon
                icon={faBoxOpen}
                className="text-blue-800 h-3.5 w-3.5"
              />
              Package type<span className="text-red-700">*</span>
            </label>
            <Select
              value={form.package_type}
              onValueChange={(value) => {
                setForm((prev) => ({ ...prev, package_type: value }));
                if (errors.package_type)
                  setErrors((prev) => ({ ...prev, package_type: "" }));
              }}
              disabled={isSubmitting || packageTypesLoading}
            >
              <SelectTrigger
                className={`w-full h-10 ${
                  errors.package_type ? "border-red-500" : "border-gray-300"
                }`}
              >
                <SelectValue
                  placeholder={
                    packageTypesLoading ? "Loading..." : "Select package type"
                  }
                />
              </SelectTrigger>
              <SelectContent>
                <div className="sticky top-0 bg-white p-2 border-b border-gray-100">
                  <div className="relative">
                    <FontAwesomeIcon
                      icon={faSearch}
                      className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400"
                    />
                    <Input
                      type="text"
                      placeholder="Search packages..."
                      value={packageSearch}
                      onChange={(e) => setPackageSearch(e.target.value)}
                      className="pl-8 pr-3 py-1.5 h-8 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-800/30 w-full"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                </div>
                {filteredPackages.map((pt) => (
                  <SelectItem key={pt} value={pt}>
                    {pt}
                  </SelectItem>
                ))}
                {filteredPackages.length === 0 && (
                  <div className="px-3 py-2 text-sm text-gray-400 text-center">
                    No package types found
                  </div>
                )}
              </SelectContent>
            </Select>
            {errors.package_type && (
              <p className="text-red-500 text-xs flex items-center gap-1">
                <span className="w-1 h-1 bg-red-500 rounded-full"></span>
                {errors.package_type}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-gray-700 font-medium flex items-center gap-2">
              <FontAwesomeIcon
                icon={faBoxes}
                className="text-blue-800 h-3.5 w-3.5"
              />
              No of items<span className="text-red-700">*</span>
            </label>
            <Input
              name="no_of_items"
              type="number"
              min="0"
              value={form.no_of_items}
              onChange={handleChange}
              placeholder="0"
              className={`border ${
                errors.no_of_items ? "border-red-500" : "border-gray-300"
              } rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-800/30`}
            />
            {errors.no_of_items && (
              <p className="text-red-500 text-xs flex items-center gap-1">
                <span className="w-1 h-1 bg-red-500 rounded-full"></span>
                {errors.no_of_items}
              </p>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default EditProduct;
